import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useFeatureCheck } from '@/hooks/useFeature';
import { useFeatureFlagEnabled } from '@/lib/analytics';

import { comparisonRerunChoices, type RerunChoice, type RerunChoiceKey } from './comparisonRerunChoices';
import { comparePartOf, type MergedRun } from './mergeRuns';
import { analysisGate } from './registry';
import { useAnalysisAvailability } from './useAnalysisAvailability';
import { useOpenComparisonRerun } from './useOpenAnalysisRerun';

/**
 * The hub's re-run menu for a paired comparison: the choices this user gets
 * (entry gate + flags + the deviation card's gate, resolved like the dialog
 * does — `comparisonRerunChoices`) and the action. A locked choice answers
 * with the upgrade dialog; an open one opens Compare surfaces with its
 * results ticked (`useOpenComparisonRerun`).
 */
export function useComparisonRerun(): {
  choices: RerunChoice[];
  rerun: (run: Pick<MergedRun, 'parts'>, key: RerunChoiceKey) => void;
} {
  const { t } = useTranslation('project');
  const availability = useAnalysisAvailability();
  const featureCheck = useFeatureCheck();
  const flags = {
    cutFill: useFeatureFlagEnabled('surfaceDifference'),
    deviation: useFeatureFlagEnabled('deviationAnalysis'),
  };
  const open = useOpenComparisonRerun();

  const entry = availability('compare');
  const entryKey = analysisGate('compare');
  const entryLock = entry.locked && entryKey ? { gate: featureCheck(entryKey).gate, promptUpgrade: entry.promptUpgrade } : null;
  const deviationGate = featureCheck('analysis.deviation');
  const choices = comparisonRerunChoices({
    visible: entry.visible,
    cutFillLock: entryLock,
    deviationLock: deviationGate.allowed ? null : deviationGate,
    flags,
  });

  const rerun = (run: Pick<MergedRun, 'parts'>, key: RerunChoiceKey) => {
    const choice = choices.find((c) => c.key === key);
    if (!choice) return;
    if (choice.lock) {
      choice.lock.promptUpgrade();
      return;
    }
    const cutFill = run.parts.find((p) => comparePartOf(p.type) === 'cutFill');
    const deviation = run.parts.find((p) => comparePartOf(p.type) === 'deviation');
    if (!cutFill || !deviation) return;
    open({ cutFillJobId: cutFill.id, deviationJobId: deviation.id }, choice.results).catch(() =>
      toast.error(t('analysisLauncher.runs.inputFailed')),
    );
  };

  return { choices, rerun };
}
